import gql from 'graphql-tag'
import { splitError } from './splitError'

const cloneDeep = require('lodash.clonedeep')

/**
 * Apollo Error Handling has a strange behaviour
 * on partial errors (status 4xx or 5xx with data and error in the response)
 * we first get our data set but afterwards overwritten by an empty object.
 * This leads to error messages of the type "cannot access property xxx of undefined"
 *
 * A way to solve this is disable automatic property binding (manual: true)
 * and mapping the response our self with the result callback.
 *
 * This helper Methods generalizes this for all components that need to use it.
 *
 * Usage:
 * apollo: {
 *  myProperty: function () {
 *    return wrapQuery(gql`query{<my custom gql query>}`, this, 'myProperty')
 *   }
 * }
 *
 * instead of apollo standard:
 * apollo: {
 *   myProperty: gql`query{<my custom gql query>}`
 * }
 *
 * it is important to use the function() - wrapping to have the vue instance in `this`
 *
 * availble options:
 * - noDeepCopy: by default the wrapper will inject a deep copy of the received object into the component
 * ..this behaviour can be disabled by setting noDeepCopy to true
 */

const defaultErrorHandler = (error, vm) => {
  if (vm.$store) {
    splitError(error).forEach(e => vm.$store.commit('addError', e))
  }
}

const wrapQueryWithErrorHandler = function (query, component, field, variables, callback, options = {}, errorHandler) {
  return {
    query: gql`${query}`,
    manual: true,
    // only maps data to field of component if it is part of the response
    // currently required to handle errors
    result ({ data, loading }) {
      if (!loading && data) {
        if (typeof field === 'string') {
          applyValue(component, field, data, options)
          if (callback) {
            callback(component[field])
          }
        } else if (Array.isArray(field)) {
          field.forEach(one => {
            applyValue(component, one, data, options)
            if (callback) {
              callback(component[one])
            }
          })
        }
      }
    },
    error (error, vm) {
      errorHandler(error, vm)
    },
    variables: variables,
    ...options
  }
}

const wrapQueryWithDefaultErrorHandling = function (query, component, field, variables, callback, options = {}, errorCallback) {
  return wrapQueryWithErrorHandler(query, component, field, variables, callback, options, (error, vm) => {
    defaultErrorHandler(error, vm)
    if (errorCallback) {
      errorCallback(error, vm)
    }
  })
}

const wrapQuery = wrapQueryWithDefaultErrorHandling

function applyValue (component, field, data, options = {}) {
  if (options.noDeepCopy === true) {
    component[field] = data[field]
  } else {
    component[field] = cloneDeep(data[field])
  }
  delete options.noDeepCopy
}

export { wrapQuery, wrapQueryWithErrorHandler, wrapQueryWithDefaultErrorHandling, defaultErrorHandler }
